/**
 * Offline Indicator Component
 * Shows current online/offline status and pending sync count
 */

import { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, RefreshCw, Check, AlertTriangle, Trash2, Clock, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getPendingOfflineSalesCount, getOfflineStats, getPendingOfflineSales, deleteSyncedSale } from '@/utils/offlineStorage';
import { subscribeOnline, probeNow } from '@/utils/connectivity';
import { 
  syncOfflineSales, 
  refreshOfflineCache, 
  onSyncStatusChange,
  onCacheStatusChange,
  startAutoCache,
  stopAutoCache,
  isAutoCacheEnabled,
  getAutoCacheInterval,
  initAutoCache,
  startAutoSync,
  stopAutoSync,
  isSyncInProgress,
} from '@/utils/syncService';
import { toast } from 'sonner';

export const OfflineIndicator = () => {
  // `isOnline` reflects whether the **backend** is reachable, not just the
  // browser's network state. We start optimistically with navigator.onLine
  // (so we don't show a red flash on cold boot) and then probe /api/health
  // every 15s. After 3 consecutive probe failures we flip to red so cashiers
  // know their tills are talking to a dead server, even if their WiFi is
  // technically up.
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [offlineStats, setOfflineStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingSales, setPendingSales] = useState([]);
  const [showPendingList, setShowPendingList] = useState(false);
  
  // Auto-cache state
  const [autoCacheEnabled, setAutoCacheEnabled] = useState(false);
  const [autoCacheInterval, setAutoCacheInterval] = useState(3600000); // 1 hour default
  const [lastAutoCache, setLastAutoCache] = useState(null);
  const [isAutoCaching, setIsAutoCaching] = useState(false);

  // Initialize auto-cache on component mount
  useEffect(() => {
    // Check if auto-cache was previously enabled
    const enabled = isAutoCacheEnabled();
    const interval = getAutoCacheInterval();
    setAutoCacheEnabled(enabled);
    setAutoCacheInterval(interval);
    
    // Initialize auto-cache if it was enabled
    if (enabled) {
      initAutoCache();
    }

    // Always run the pending-sales auto-sync in the background — the
    // cashiers were short at cash-up because the till kept pending
    // sales queued (manual-sync-only) and nobody remembered to flush
    // them. 60s cadence is fast enough that nothing ever sits for a
    // full till shift, and `startAutoSync` already no-ops when offline
    // or when a sync is already running, so it's safe to leave on.
    startAutoSync(60000);
    return () => stopAutoSync();
  }, []);

  // Monitor online/offline status — subscribe to the shared probe loop
  // in `utils/connectivity.js` instead of running our own fetch(/api/health)
  // every 15s. Previously BOTH this component AND connectivity.js probed
  // independently, which doubled the backend status traffic on every till
  // (4 tills open = 8 probes/sec just for status). The shared loop has
  // its own 3-fail hysteresis so flicker is already handled.
  useEffect(() => {
    const handleOffline = () => {
      toast.warning('You are offline', { description: 'Sales will be saved locally' });
    };
    window.addEventListener('offline', handleOffline);

    let wasOnline = navigator.onLine;
    const unsubscribe = subscribeOnline(async (nowOnline) => {
      setIsOnline(nowOnline);
      if (nowOnline && !wasOnline) {
        toast.success('Back online!', { description: 'Server connection restored' });
        checkPendingSales();
        // The native `window.online` event doesn't fire when ONLY the
        // backend recovers (OS network state never changed), so startAutoSync's
        // own online-listener alone would never trigger. Kick an immediate
        // sync here so cash-up isn't short because the server came back
        // and nobody noticed.
        try {
          const pending = await getPendingOfflineSalesCount();
          if (pending > 0 && !isSyncInProgress()) {
            syncOfflineSales({ autoMode: true }).then((r) => {
              if (r?.success && r?.synced > 0) {
                toast.success(`Auto-synced ${r.synced} pending sale${r.synced > 1 ? 's' : ''}`);
                checkPendingSales();
                loadOfflineStats();
              }
            });
          }
        } catch { /* best-effort */ }
      }
      wasOnline = nowOnline;
    });

    // Check initial pending count
    checkPendingSales();
    loadOfflineStats();

    // Listen for sync status changes
    const unsubscribeSync = onSyncStatusChange((status) => {
      if (status.syncing !== undefined) setSyncing(status.syncing);
      if (status.progress !== undefined) setSyncProgress(status.progress);
      if (status.pendingSales !== undefined) setPendingCount(status.pendingSales);
    });

    // Listen for cache status changes
    const unsubscribeCache = onCacheStatusChange((status) => {
      if (status.caching !== undefined) setIsAutoCaching(status.caching);
      if (status.lastCache) {
        setLastAutoCache(status.lastCache);
        loadOfflineStats(); // Refresh stats after cache
      }
    });

    // Periodically check pending sales
    const interval = setInterval(checkPendingSales, 30000);

    return () => {
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
      unsubscribe();
      unsubscribeSync();
      unsubscribeCache();
    };
  }, []);

  const checkPendingSales = async () => {
    try {
      const count = await getPendingOfflineSalesCount();
      setPendingCount(count);
      // Also load the actual sales list
      const sales = await getPendingOfflineSales();
      setPendingSales(sales);
    } catch (error) {
      console.error('Error checking pending sales:', error);
    }
  };

  const loadOfflineStats = async () => {
    try {
      const stats = await getOfflineStats();
      setOfflineStats(stats);
    } catch (error) {
      console.error('Error loading offline stats:', error);
    }
  };

  const handleDeletePendingSale = async (offlineId) => {
    try {
      // Delete from IndexedDB
      const db = await new Promise((resolve, reject) => {
        const request = indexedDB.open('pos_offline_db', 1);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      const transaction = db.transaction('offline_sales', 'readwrite');
      const store = transaction.objectStore('offline_sales');
      
      await new Promise((resolve, reject) => {
        const request = store.delete(offlineId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
      
      toast.success('Pending sale deleted');
      checkPendingSales();
      loadOfflineStats();
    } catch (error) {
      console.error('Error deleting pending sale:', error);
      toast.error('Failed to delete pending sale');
    }
  };

  const handleClearAllPending = async () => {
    if (!confirm('Are you sure you want to delete ALL pending offline sales? This cannot be undone.')) {
      return;
    }
    
    try {
      const db = await new Promise((resolve, reject) => {
        const request = indexedDB.open('pos_offline_db', 1);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      const transaction = db.transaction('offline_sales', 'readwrite');
      const store = transaction.objectStore('offline_sales');
      
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
      
      toast.success('All pending sales cleared');
      checkPendingSales();
      loadOfflineStats();
      setShowPendingList(false);
    } catch (error) {
      console.error('Error clearing pending sales:', error);
      toast.error('Failed to clear pending sales');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress(0);

    const result = await syncOfflineSales({
      onProgress: ({ progress, synced, failed, total }) => {
        setSyncProgress(progress);
      },
    });

    setSyncing(false);

    if (result.success) {
      toast.success('Sync Complete', {
        description: result.message,
      });
      checkPendingSales();
      loadOfflineStats();
    } else {
      toast.error('Sync Failed', {
        description: result.message || 'Check pending sales for error details',
        duration: 8000,
      });
      // Refresh the pending list to show error details
      checkPendingSales();
    }

    setShowSyncDialog(false);
  };

  const handleRefreshCache = async () => {
    setRefreshing(true);

    const result = await refreshOfflineCache((progress) => {
      // Could show progress here
    });

    setRefreshing(false);

    if (result.success) {
      toast.success('Cache Updated', {
        description: result.message,
      });
      loadOfflineStats();
    } else {
      toast.error('Cache Update Failed', {
        description: result.message,
      });
    }
  };

  const handleAutoCacheToggle = (enabled) => {
    setAutoCacheEnabled(enabled);
    if (enabled) {
      startAutoCache(autoCacheInterval);
      toast.success('Auto-Cache Enabled', {
        description: `Cache will refresh every ${autoCacheInterval / 60000} minutes`,
      });
    } else {
      stopAutoCache();
      toast.info('Auto-Cache Disabled');
    }
  };

  const handleIntervalChange = (value) => {
    const interval = parseInt(value, 10);
    setAutoCacheInterval(interval);
    if (autoCacheEnabled) {
      startAutoCache(interval);
      toast.success('Interval Updated', {
        description: `Cache will now refresh every ${interval / 60000} minutes`,
      });
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleString();
  };

  return (
    <>
      {/* Compact indicator in header */}
      <div className="flex items-center gap-2">
        {/* Auto-cache indicator */}
        {autoCacheEnabled && (
          <div 
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isAutoCaching ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}
            title={`Auto-cache: ${autoCacheEnabled ? 'ON' : 'OFF'} - Last: ${formatTimeAgo(offlineStats?.lastSyncTime)}`}
          >
            <Clock className={`h-3 w-3 ${isAutoCaching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Auto</span>
          </div>
        )}
        
        {/* Online/Offline status */}
        <div
          className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium cursor-pointer ${
            isOnline
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700 animate-pulse'
          }`}
          onClick={() => setShowSyncDialog(true)}
          title={isOnline ? 'Online' : 'Offline - Sales will be saved locally'}
        >
          {isOnline ? (
            <Wifi className="h-3 w-3" />
          ) : (
            <WifiOff className="h-3 w-3" />
          )}
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </div>

        {/* Pending sales indicator */}
        {pendingCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => setShowSyncDialog(true)}
          >
            <CloudOff className="h-3 w-3 mr-1" />
            {pendingCount} pending
          </Button>
        )}
      </div>

      {/* Sync Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={(open) => {
        setShowSyncDialog(open);
        if (open) {
          // Refresh data when dialog opens
          checkPendingSales();
          loadOfflineStats();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isOnline ? (
                <Wifi className="h-5 w-5 text-green-600" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-600" />
              )}
              Offline Sales & Sync
            </DialogTitle>
            <DialogDescription>
              {isOnline
                ? 'You are online. Sync your offline sales or update the local cache.'
                : 'You are offline. Sales will be saved locally and synced when you reconnect.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Auto-Cache Settings */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="auto-cache" className="font-medium text-blue-800">
                    Auto-Cache
                  </Label>
                </div>
                <Switch
                  id="auto-cache"
                  checked={autoCacheEnabled}
                  onCheckedChange={handleAutoCacheToggle}
                />
              </div>
              
              {autoCacheEnabled && (
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-xs text-blue-700">Refresh every:</Label>
                  <Select value={autoCacheInterval.toString()} onValueChange={handleIntervalChange}>
                    <SelectTrigger className="h-8 w-32 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="900000">15 minutes</SelectItem>
                      <SelectItem value="1800000">30 minutes</SelectItem>
                      <SelectItem value="3600000">1 hour</SelectItem>
                      <SelectItem value="7200000">2 hours</SelectItem>
                      <SelectItem value="14400000">4 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <p className="text-xs text-blue-600 mt-2">
                {autoCacheEnabled 
                  ? `✓ Products & customers will auto-update every ${autoCacheInterval / 60000} min`
                  : 'Enable to automatically keep offline data fresh'}
              </p>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Cached Products</p>
                <p className="text-xl font-bold">{offlineStats?.cachedProducts || 0}</p>
                <p className="text-xs text-gray-400">
                  {offlineStats?.cachedCustomers || 0} customers
                </p>
              </div>
              <div className={`p-3 rounded-lg ${pendingCount > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                <p className="text-xs text-gray-500 mb-1">Pending Sales</p>
                <p className={`text-xl font-bold ${pendingCount > 0 ? 'text-amber-600' : ''}`}>
                  {pendingCount}
                </p>
              </div>
            </div>

            {/* Last sync time */}
            {offlineStats?.lastSyncTime && (
              <p className="text-xs text-gray-500">
                Last cache update: {formatTimeAgo(offlineStats.lastSyncTime)}
              </p>
            )}

            {/* Sync Progress */}
            {syncing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Syncing...</span>
                  <span>{syncProgress}%</span>
                </div>
                <Progress value={syncProgress} />
              </div>
            )}

            {/* Offline mode warning */}
            {!isOnline && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Working Offline</p>
                  <p className="text-sm text-amber-700">
                    You can continue making sales. They will be saved locally and synced when you're back online.
                  </p>
                </div>
              </div>
            )}

            {/* Ready status */}
            {offlineStats?.isReady && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="h-4 w-4" />
                <span>Ready for offline sales ({offlineStats.cachedProducts} products cached)</span>
              </div>
            )}

            {/* Not ready warning */}
            {!offlineStats?.isReady && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Not Ready for Offline</p>
                  <p className="text-sm text-red-700">
                    Click "Update Cache" to download products for offline use.
                  </p>
                </div>
              </div>
            )}

            {/* Pending Sales List - Always show section if there might be pending sales */}
            <div className="border rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <p className="font-medium text-sm">Pending Offline Sales: {pendingCount}</p>
                <div className="flex gap-2">
                  {pendingCount > 0 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setShowPendingList(!showPendingList)}
                      >
                        {showPendingList ? 'Hide' : 'Show'} Details
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={handleClearAllPending}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Clear All
                      </Button>
                    </>
                  )}
                  {pendingCount === 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-gray-500"
                      onClick={handleClearAllPending}
                      title="Force clear any stuck sales"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Force Clear
                    </Button>
                  )}
                </div>
              </div>
              
              {showPendingList && pendingSales.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {pendingSales.map((sale, idx) => (
                    <div key={sale.offline_id} className="p-2 bg-gray-50 rounded text-xs">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">
                            R {(sale.total || 0).toFixed(2)} - {sale.transaction_type || 'CASH SALE'}
                          </p>
                          <p className="text-gray-500">
                            {new Date(sale.created_at).toLocaleString()} • {sale.items?.length || 0} items
                          </p>
                          {sale.sync_attempts > 0 && (
                            <p className="text-red-600 mt-1">
                              Failed {sale.sync_attempts}x: {sale.last_sync_error || 'Unknown error'}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeletePendingSale(sale.offline_id)}
                          title="Delete this sale"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleRefreshCache}
              disabled={!isOnline || refreshing}
            >
              {refreshing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Update Cache
            </Button>

            <Button
              onClick={handleSync}
              disabled={!isOnline || syncing || pendingCount === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {syncing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Sync {pendingCount > 0 ? `(${pendingCount})` : 'Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OfflineIndicator;
